import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2, Pencil, Save, Send, Trash2, X } from 'lucide-react';
import { adminApi } from '../../services/api';
import Fenetre from '../Fenetre';
import {
  boutonPrincipal, boutonSecondaire, Carte, champ, Chargement, Erreur, fmtDate, Pastille, TexteLignes, Vide,
} from './ui';
import { annoncerErreur, annoncerSucces, confirmer, EtatErreur } from '../../etats';

const RUBRIQUES = [
  ['objectifs', 'Objectifs de la période'],
  ['activites', 'Activités réalisées'],
  ['progres', 'Progrès constatés'],
  ['difficultes', 'Difficultés rencontrées'],
  ['recommandations', 'Recommandations'],
  ['prochaines_etapes', 'Prochaines étapes'],
];

const APPRECIATIONS = {
  tres_satisfaisant: 'Très satisfaisant',
  satisfaisant: 'Satisfaisant',
  a_surveiller: 'À surveiller',
  preoccupant: 'Préoccupant',
};

function Editeur({ initial, seances, onAnnuler, onEnregistrer, onEnvoyer }) {
  const [v, setV] = useState(() => ({
    titre: '', periode_debut: '', periode_fin: '', appreciation: '', partage_association: false, seance_id: '',
    ...Object.fromEntries(RUBRIQUES.map(([cle]) => [cle, ''])),
    ...(initial || {}),
  }));
  const [etat, setEtat] = useState('');
  const [erreur, setErreur] = useState('');
  const maj = (cle) => (e) => setV((x) => ({ ...x, [cle]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const donnees = () => ({
    titre: v.titre, periode_debut: v.periode_debut || '', periode_fin: v.periode_fin || '', appreciation: v.appreciation,
    partage_association: v.partage_association, seance_id: v.seance_id || '',
    ...Object.fromEntries(RUBRIQUES.map(([cle]) => [cle, v[cle]])),
  });

  const agir = async (quoi) => {
    if (!v.titre.trim()) { setErreur('Donnez un titre au rapport.'); return; }
    setEtat(quoi);
    setErreur('');
    try {
      if (quoi === 'envoi') {
        if (!await confirmer("Envoyer ce rapport ? Il ne pourra plus être modifié.")) { setEtat(''); return; }
        await onEnvoyer(donnees());
        annoncerSucces("Rapport envoyé à l'administration.");
      } else {
        await onEnregistrer(donnees());
        annoncerSucces('Brouillon enregistré.');
        setEtat('');
      }
    } catch (e) {
      setErreur(e.message);
      setEtat('');
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-4 gap-3">
        <label className="sm:col-span-2 block text-xs font-semibold text-[var(--text-muted)]">Titre *
          <input value={v.titre} onChange={maj('titre')} placeholder="Ex. Rapport de coaching — septembre" className={`${champ} mt-1`} />
        </label>
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Du
          <input type="date" value={v.periode_debut || ''} onChange={maj('periode_debut')} className={`${champ} mt-1`} />
        </label>
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Au
          <input type="date" value={v.periode_fin || ''} onChange={maj('periode_fin')} className={`${champ} mt-1`} />
        </label>
      </div>
      {seances.length > 0 && (
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Séance concernée (facultatif)
          <select value={v.seance_id || ''} onChange={maj('seance_id')} className={`${champ} mt-1`}>
            <option value="">— Aucune en particulier —</option>
            {seances.map((s) => <option key={s.id} value={s.id}>{fmtDate(s.date_debut)} · {s.objet}</option>)}
          </select>
        </label>
      )}
      {RUBRIQUES.map(([cle, libelle]) => (
        <label key={cle} className="block text-xs font-semibold text-[var(--text-muted)]">{libelle}
          <textarea value={v[cle]} onChange={maj(cle)} rows={3} className={`${champ} mt-1 resize-y`} />
        </label>
      ))}
      <div className="grid sm:grid-cols-2 gap-3 items-end">
        <label className="block text-xs font-semibold text-[var(--text-muted)]">Appréciation d'ensemble
          <select value={v.appreciation} onChange={maj('appreciation')} className={`${champ} mt-1`}>
            <option value="">— Non renseignée —</option>
            {Object.entries(APPRECIATIONS).map(([cle, libelle]) => <option key={cle} value={cle}>{libelle}</option>)}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-[var(--text-main)] pb-2.5">
          <input type="checkbox" checked={!!v.partage_association} onChange={maj('partage_association')} />
          Partager aussi ce rapport avec l'association
        </label>
      </div>
      <Erreur message={erreur} />
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onAnnuler} className={boutonSecondaire}><X className="w-4 h-4" />Fermer</button>
        <button type="button" onClick={() => agir('brouillon')} disabled={!!etat} aria-busy={etat === 'brouillon' || undefined} className={boutonSecondaire}>
          {etat === 'brouillon' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer le brouillon
        </button>
        <button type="button" onClick={() => agir('envoi')} disabled={!!etat} aria-busy={etat === 'envoi' || undefined} className={boutonPrincipal}>
          {etat === 'envoi' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Envoyer à l'administration
        </button>
      </div>
    </div>
  );
}

/* Rapports de coaching : rediges par le coach, transmis a l'administration
   et, s'il le choisit, a l'association. */
export default function RapportsProjet({ projetId, peutAgir, utilisateur }) {
  const [rapports, setRapports] = useState(null);
  const [seances, setSeances] = useState([]);
  const [edition, setEdition] = useState(null); // null | 'nouveau' | rapport
  // Cle de l'editeur, changee seulement a l'ouverture : enregistrer un nouveau
  // rapport ne doit pas remonter le formulaire, et les essais suivants doivent
  // modifier ce brouillon au lieu d'en creer un autre.
  const [cleEditeur, setCleEditeur] = useState(0);
  const ouvrir = (rapport) => { setEdition(rapport); setCleEditeur((c) => c + 1); };
  const [ouvert, setOuvert] = useState(null);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(() => {
    adminApi.getRapports(projetId)
      .then((r) => { setRapports(r); setErreur(null); })
      .catch((e) => setErreur(e));
  }, [projetId]);

  useEffect(() => {
    charger();
    if (peutAgir) adminApi.getSeances(projetId).then(setSeances).catch(() => setSeances([]));
  }, [charger, peutAgir, projetId]);

  const enregistrer = async (rapport, donnees) => (
    rapport?.id ? adminApi.updateRapport(rapport.id, donnees) : adminApi.createRapport(projetId, donnees)
  );

  if (!rapports) return erreur ? <EtatErreur erreur={erreur} onReessayer={charger} /> : <Chargement />;
  const modifiable = (r) => r.statut === 'brouillon' && (r.auteur_id === utilisateur?.id || ['SUPER_ADMIN', 'ADMIN'].includes(utilisateur?.role));

  return (
    <div className="space-y-4">
      {peutAgir && edition && (
        <Fenetre largeur="large" icone={FileText} onClose={() => setEdition(null)}
          titre={edition === 'nouveau' ? 'Nouveau rapport de coaching' : 'Rapport de coaching'}>
          <Editeur
            key={cleEditeur}
            initial={edition === 'nouveau' ? null : edition}
            seances={seances}
            onAnnuler={() => setEdition(null)}
            onEnregistrer={async (d) => { const r = await enregistrer(edition === 'nouveau' ? null : edition, d); setEdition(r); charger(); }}
            onEnvoyer={async (d) => {
              const r = await enregistrer(edition === 'nouveau' ? null : edition, d);
              setEdition(r);
              await adminApi.envoyerRapport(r.id, { partage_association: d.partage_association });
              setEdition(null);
              charger();
            }}
          />
        </Fenetre>
      )}
      {peutAgir && (
        <Carte titre="Rapports de coaching"
          action={<button type="button" onClick={() => ouvrir('nouveau')} className={boutonPrincipal}><FileText className="w-4 h-4" />Rédiger un rapport</button>}>
          <p className="text-sm text-[var(--text-muted)]">Le rapport est enregistré en brouillon tant qu'il n'est pas envoyé. Une fois envoyé, l'administration est prévenue et il n'est plus modifiable.</p>
        </Carte>
      )}

      {rapports.length === 0 ? (
        <Vide icone={FileText} titre="Aucun rapport" texte="Les rapports de coaching de ce projet apparaîtront ici." />
      ) : (
        <ul className="space-y-3">
          {rapports.map((r) => (
            <li key={r.id} className="bg-[var(--bg-card)] shadow-carte rounded-carte p-5">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <button type="button" className="act act-tertiaire text-left min-w-0" onClick={() => setOuvert(ouvert === r.id ? null : r.id)} aria-expanded={ouvert === r.id}>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Pastille className={r.statut === 'envoye' ? 'text-[var(--vert-fonce)] bg-[var(--vert-pale)] border-[var(--border)]' : 'text-[var(--text-main)] bg-[var(--bg-main)] border-[var(--border)]'}>{r.statut_libelle}</Pastille>
                    {r.appreciation_libelle && <Pastille className="text-[var(--accent-texte)] bg-[var(--accent)]/10 border-[var(--accent)]/30">{r.appreciation_libelle}</Pastille>}
                    {r.partage_association && <span className="text-xs text-[var(--text-muted)]">partagé avec l'association</span>}
                  </div>
                  <p className="font-semibold text-[var(--text-bold)]">{r.titre}</p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {r.auteur}{r.periode_debut ? ` · du ${fmtDate(r.periode_debut)} au ${fmtDate(r.periode_fin)}` : ''} · {r.envoye_le ? `envoyé le ${fmtDate(r.envoye_le)}` : `modifié le ${fmtDate(r.updated_at)}`}
                  </p>
                </button>
                {modifiable(r) && peutAgir && (
                  <div className="flex gap-1 shrink-0">
                    <button type="button" onClick={() => ouvrir(r)} aria-label="Modifier le rapport" className="act act-icone act-tertiaire"><Pencil className="w-4 h-4" /></button>
                    <button type="button" aria-label="Supprimer le brouillon"
                      onClick={async () => { if (!await confirmer('Supprimer ce brouillon ?')) return; try { await adminApi.deleteRapport(r.id); annoncerSucces('Brouillon supprimé.'); charger(); } catch (e) { annoncerErreur(e.message || "Le brouillon n'a pas pu être supprimé."); } }}
                      className="act act-icone act-tertiaire"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              {ouvert === r.id && (
                <dl className="mt-4 space-y-3">
                  {RUBRIQUES.filter(([cle]) => r[cle]).map(([cle, libelle]) => (
                    <div key={cle}>
                      <dt className="surtitre text-[var(--text-muted)] mb-0.5">{libelle}</dt>
                      <dd><TexteLignes texte={r[cle]} className="text-sm text-[var(--text-main)]" /></dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}